import type {
  OrderDetail as PanelOrder,
  OrderDetailItem as PanelOrderItem,
  OrderDetailPanelProps,
  OrderDetailStatus as PanelStatus,
} from '@engyalo/delivery-ui-components'
import type { IOrderDetail } from '../graphQL/queries/order/types'
import type { OrderDetailStatus as SliceStatus } from '../redux/slices/orders/types'
import type { RootState } from '../redux/store'
import { isHidePricesEnabled } from './hidePrices'
import {
  deriveOrderOutcome,
  isOrderPending,
  resolveOrderConfirmation,
  showsCallbackDeclineReason,
} from './orderConfirmation'
import { toDeclineReason } from './orderConfirmationPanel'
import { getOrderDeclineReason } from '../hooks/orders/ordersHookHelpers'
import {
  getOrderPaymentPickAttributes,
  getOrderPaymentPickLabel,
} from './orderPaymentPickLabel'
import {
  formatTermDaysTag,
  selectTermDaysTagTemplate,
} from './paymentTermDaysTag'
import { toIsoDate } from './toIsoDate'

/**
 * The slice statuses that are also panel statuses, as a total map.
 *
 * Typed both ways on purpose. The key type is the slice's own union, so
 * dropping or adding a member there fails the build here rather than silently
 * falling through; the value type is the library's, so a status the panel does
 * not publish cannot be sent to it.
 *
 * `idle` is the member that makes this a map rather than a cast. It is not a
 * `OrderDetailStatus` member, and `OrderDetailPanel` defaults an unrecognised
 * `status` to `loading` — so an unmapped `idle` is not a type error at the
 * call site, it is a skeleton that never resolves, on the screen a shopper
 * just opened. Mapping it to `loading` is the same picture with an end to it.
 *
 * `pending` and `timeout` are absent because no slice status carries them:
 * both are derived below.
 * @internal
 */
const PANEL_STATUS_BY_SLICE_STATUS: Record<SliceStatus, PanelStatus> = {
  idle: 'loading',
  loading: 'loading',
  ready: 'ready',
  error: 'error',
  notFound: 'notFound',
  sessionEnded: 'sessionEnded',
}

/**
 * The panel status for the order currently on the Detail screen.
 *
 * The single place the derivation lives. Everything outside `ready` is a
 * lookup; `ready` is where the two states the slice does not name are worked
 * out from the order and the storefront's confirmation config.
 *
 * **`pending` and `timeout` only exist for an async storefront.** They
 * describe a confirmation the storefront is waiting on, so where nothing
 * confirms asynchronously (`orderConfirmation` absent, or `required` anything
 * but `true`) the order renders as soon as it resolves and neither state is
 * reachable. That is the sync path, and it is the default.
 *
 * **The client giving up and the server reporting `TIMEOUT` are different
 * facts, deliberately collapsed here.** `pollTimedOut` is our own deadline;
 * `externalRefStatus: 'TIMEOUT'` is the downstream system's verdict, and
 * either can happen without the other. The panel publishes one `timeout` slot
 * and one message for it, so the distinction has nowhere to land in the UI —
 * do not reintroduce it here on the strength of the two fields existing.
 * Datadog and the slice still carry which one occurred.
 */
export const selectOrderDetailPanelStatus = (state: RootState): PanelStatus => {
  const { selectedStatus, selected, pollTimedOut } = state.ordersSlice
  const status = PANEL_STATUS_BY_SLICE_STATUS[selectedStatus]

  if (status !== 'ready') return status

  const orderConfirmation =
    state.defaultSlice.sessionData?.configuration?.order?.confirmation
  const { isAsync } = resolveOrderConfirmation(orderConfirmation)
  if (!isAsync) return 'ready'

  if (pollTimedOut || selected?.externalRefStatus === 'TIMEOUT') {
    return 'timeout'
  }
  /*
   * Pending means the order is *both* pending by the predicate and has not
   * reached a status the storefront calls terminal. Both halves are load-bearing.
   *
   * The predicate half is read off the order, not off `isPollingOrder`: that
   * flag is true only while a tick is in flight, so between ticks a
   * still-pending order would flip back to `ready` and the confirming copy
   * would blink.
   *
   * The outcome half is what stops `externalRefStatus: PROCESSING` outranking
   * a status that settled. `createOrderAddon` sets `PROCESSING`
   * unconditionally and only the callback handler ever clears it, while the
   * native `confirmOrder` sets `status: CONFIRMED` without touching it — so on
   * an add-on storefront a confirmed order carries both, and the predicate
   * alone renders it as "confirming" for as long as the shopper looks at it.
   *
   * `deriveOrderOutcome` is not used on its own because its own last check
   * reads a bare `CREATED` order on an async storefront as pending. That is
   * the right answer for the *flow*, which is deciding whether to keep
   * polling, and the wrong one here: this screen is often opened long after
   * the order was placed, and a `CREATED` order nothing is going to move must
   * not show a confirming spinner forever.
   */
  const settled = deriveOrderOutcome(selected, orderConfirmation) !== 'pending'
  if (isOrderPending(selected) && !settled) return 'pending'

  return 'ready'
}

/**
 * The depot name to draw under Order Detail's distributor heading, or `null`
 * to draw no row at all (COM-889).
 *
 * **Resolution does not happen here, and cannot.** This function is a pure,
 * synchronous read of the store, and turning `Order.storeCode` into a depot
 * name is a `getStores` round trip. `useOrderDistributor` runs it and writes
 * the answer into the slice; this is the read half. See that hook's docstring
 * for why the work sits there rather than in `useGetOrderDetail`.
 *
 * **The code comparison is the read-side half of the safety property**, and
 * saying which half matters. `selectedDistributor` carries the code it was
 * resolved from, and a name is only rendered while that code still matches the
 * order on screen. What that alone buys: a name resolved for the previous
 * order cannot appear under this one, and a poll tick replacing `selected`
 * with the same `storeCode` leaves the row untouched rather than blanking it.
 * Delete the comparison and both of those have to be solved again, in a
 * reducer, one at a time.
 *
 * **The other half is `requestIdRef` in `useOrderDistributor`, and it is not
 * this.** An earlier revision of this comment credited the comparison with
 * also handling a lookup that lands after the shopper has opened a different
 * order. It does not: without the token that response still *dispatches*, and
 * the stale pair overwrites the current order's. The comparison then hides the
 * row rather than showing the wrong one — better than nothing, but the row is
 * lost until something re-resolves it. Put another way, the comparison stops a
 * **wrong** row and the token stops a **lost** one. The hook's own
 * `requestIdRef` docstring has always said this; two documents in the same
 * change credited one property to different mechanisms.
 *
 * **`null` rather than the code, on every unhappy path.** Not yet resolved, no
 * such store, `getStores` failed, order absent, order carrying no
 * `storeCode` — one answer for all five, which is no row. A raw `'5887'` under
 * a heading reading "Distribuidor" is worse than silence, and a placeholder or
 * a spinner would promise something arriving that may never arrive. The
 * consequence a reader should expect is a row that **appears late**: the order
 * renders first and the distributor joins it a round trip later. That is the
 * accepted cost of not blocking the order on a secondary lookup.
 *
 * The name is passed through **verbatim**, whitespace included. The live
 * values carry a leading space (`' MDLZ CEVE CDMX 1 OTE DU'`) and the library
 * normalises with `String(value ?? '').trim()` before both its emptiness test
 * and its render, so trimming here would duplicate that and hide it. Blank and
 * whitespace-only names are filtered out upstream, in the hook, so they never
 * reach the slice; if one ever did, the panel would still draw nothing.
 */
export const selectOrderDistributor = (state: RootState): string | null => {
  const { selected, selectedDistributor } = state.ordersSlice
  const code = (selected?.storeCode ?? '').trim()
  if (!code || !selectedDistributor) return null
  return selectedDistributor.code === code ? selectedDistributor.name : null
}

/** `null` reads as "the server sent nothing here", which the panel spells `undefined`. */
const optional = <T>(value: T | null | undefined): T | undefined =>
  value ?? undefined

/**
 * Total discount, summed from `Order.discountsByPromotion`.
 *
 * The map is `JSON` on the schema and keyed by promotion id, so its leaves are
 * unknown until read. Non-numeric ones are skipped rather than coerced: a
 * single null would make the sum `NaN`, and `Price`'s `hideOnZero` compares
 * against `0`, so a `NaN` renders as a discount row reading nothing.
 *
 * An empty or absent map yields `undefined`, not `0`, so the row does not draw
 * at all for orders that had no discounts.
 */
const sumDiscounts = (
  discountsByPromotion?: Record<string, unknown> | null,
): number | undefined => {
  if (!discountsByPromotion) return undefined
  const amounts = Object.values(discountsByPromotion).filter(
    (amount): amount is number =>
      typeof amount === 'number' && Number.isFinite(amount),
  )
  if (!amounts.length) return undefined
  return amounts.reduce((total, amount) => total + amount, 0)
}

/**
 * One order line, in the panel's shape.
 *
 * `priceTotal` is derived here because `CartProduct` carries no such field and
 * the panel renders `priceTotal ?? price` — without it, a line ordered twice
 * shows the unit price beside a quantity of two.
 *
 * The line's own `discounts`, `unitPrice`, `packageType` and `description` are
 * dropped rather than passed through. The panel declares none of them, so they
 * would render nowhere; and `discounts` is money, which is not worth handing
 * to a component that cannot show it.
 */
const toPanelItem = (item: {
  sku: string
  quantity: number
  name?: string | null
  price?: number | null
  imageURL?: string[] | null
  customFields?: Record<string, unknown> | null
}): PanelOrderItem => {
  const { sku, quantity, name, price, imageURL, customFields } = item
  const hasLineTotal =
    typeof price === 'number' &&
    Number.isFinite(price) &&
    Number.isFinite(quantity)

  return {
    sku,
    quantity,
    name: optional(name),
    price: optional(price),
    priceTotal: hasLineTotal ? price * quantity : undefined,
    imageURL: optional(imageURL),
    customFields: optional(customFields),
  }
}

/**
 * The `order` query's payload in the panel's shape.
 *
 * Two fields are computed rather than copied — `discounts` and each line's
 * `priceTotal` — because the library documents both as derived client-side and
 * cinnamon is the client. Everything else is a rename-free copy with the
 * schema's nulls flattened to `undefined`.
 *
 * `subtotal` is **not** passed through, and no longer for want of the field:
 * COM-739 put it on `Order` and it is selected and typed. Wiring it to the
 * panel is a rendering decision this ticket does not make, and the reason to
 * be careful is that the panel's gate is **opt-out**, not opt-in:
 * `fieldsToDisplay?.[field] !== false`. So adding the line below would light a
 * new money row on every storefront that has not explicitly set
 * `subtotal: false` (or `totals: false`).
 *
 * It would at least be **labelled**: `texts.orders.detail.subtotalLabel` ships
 * in all seven `defaultTexts` files, so a config that merged the defaults
 * renders a real caption. The opt-out gate is the reason for care here, not a
 * missing label — an earlier revision of this comment claimed the row would
 * render unlabelled, which was true before #730 added the namespace and is a
 * cost this decision no longer carries. Weigh the change against one reason,
 * not two.
 *
 * The old-order case is the benign half: `subtotal` reads `null` on anything
 * placed before COM-739, `optional` flattens that to `undefined`, and
 * `isRenderableRow` drops the row silently. Adding
 * `subtotal: optional(order.subtotal)` below is all it takes when someone owns
 * that call; the name is already on `maskPricesForDisplay`'s allowlist (in
 * this repo, `utils/maskPricesForDisplay.ts`), so it would mask like the other
 * money props.
 *
 * `discountsByPromotion` and `compensations` are deliberately **not** passed
 * through. The first is money under arbitrary promotion-id keys, which
 * `maskPricesForDisplay` cannot recognise and so would hand unmasked to a
 * hide-prices shopper; the second the panel does not render. Neither is worth
 * carrying for a component that would only ignore it.
 */
export const toOrderDetailPanelOrder = (
  order?: IOrderDetail | null,
): PanelOrder | undefined => {
  if (!order) return undefined

  return {
    id: order.id,
    sequence: optional(order.sequence),
    status: optional(order.status),
    // Both dates arrive as the `Date` scalar's `{ timestamp, string }` object,
    // so they are normalised rather than copied. `optional` alone would hand
    // the object straight to the panel, which renders it as no date at all
    // (COM-823).
    createdAt: toIsoDate(order.createdAt),
    expectedDeliveryDate: toIsoDate(order.expectedDeliveryDate),
    paymentMethod: optional(order.paymentMethod),
    customer: optional(order.customer)
      ? {
          address: optional(order.customer?.address),
          city: optional(order.customer?.city),
          state: optional(order.customer?.state),
          country: optional(order.customer?.country),
          zipCode: optional(order.customer?.zipCode),
          region: optional(order.customer?.region),
        }
      : undefined,
    items: Array.isArray(order.items)
      ? order.items.filter(Boolean).map(toPanelItem)
      : undefined,
    total: optional(order.total),
    totalFee: optional(order.totalFee),
    activeFees: optional(order.activeFees),
    discounts: sumDiscounts(order.discountsByPromotion),
    customFields: optional(order.customFields),
  }
}

/**
 * The `OrderDetailPanel` props cinnamon owns rather than the config author.
 *
 * Injected in `useCinnamonProps` for any component whose `importRoute` is
 * `OrderDetailPanel`, the way the `MinAmount` progress message already is.
 * None of them can be authored as a `{{variable}}`, for one of three reasons:
 * it is derived from state the config DSL cannot compute (`status`,
 * `hidePrices`), it is a shape the DSL cannot build (`order`), or it is not in
 * any state the DSL can address at all because it comes from a second network
 * call (`distributor`). `timeZone` is the one that could arguably be authored
 * and is injected anyway, so the panel's tenant-invariant props arrive from
 * one place.
 *
 * The list is deliberately not counted here. `InjectedDetailProps` below is
 * the enumeration, and it is checked against the library's own prop type, so
 * a prose count is a claim that can go stale without failing anything.
 *
 * Everything else on the panel — labels, `messages`, `statusMap`, `currency`,
 * `fieldsToDisplay`, the custom slots and the actions — stays in config. This
 * adds no layout decision to cinnamon.
 *
 * The values are injected **before** `processData` and `maskPricesForDisplay`
 * run, so the order goes through masking with everything else. Injecting
 * afterwards would hand a hide-prices shopper real totals. The exception is
 * `VERBATIM_DETAIL_PROPS`, server text assigned after resolution.
 */
/**
 * The props this file owns, checked against the panel that receives
 * them — see `InjectedConfirmationProps` in `orderConfirmationPanel.ts` for
 * why the injector's own return type is where this check has to live.
 */
type InjectedDetailProps = Pick<
  OrderDetailPanelProps,
  | 'order'
  | 'status'
  | 'hidePrices'
  | 'timeZone'
  | 'distributor'
  | 'paymentMethodLabel'
  | 'paymentMethodTag'
  | 'declineReason'
>

/**
 * Injected props that reach the panel verbatim, never through `processData`.
 * Same reason as `VERBATIM_CONFIRMATION_PROPS`: server text, where a `{{…}}`
 * is the server's words and resolving it could recurse.
 * @internal
 */
export const VERBATIM_DETAIL_PROPS = [
  'declineReason',
] as const satisfies readonly (keyof InjectedDetailProps)[]

/**
 * The client system's refusal text for the order on screen, by the
 * confirmation screen's rule (COM-1749): only where the storefront opted in
 * (`options.showCallbackDeclineReason`) and the order's `externalRefStatus` is
 * a refusal, then trimmed and capped. The panel has no failed status to gate
 * on, so this is the whole gate; the panel still hides it under `hidePrices`.
 */
const selectOrderDetailDeclineReason = (
  state: RootState,
): string | undefined => {
  const order = state.ordersSlice.selected
  if (!order) {
    return undefined
  }
  return toDeclineReason(
    getOrderDeclineReason({
      order,
      enabled: showsCallbackDeclineReason(state.defaultSlice.config?.options),
    }),
  )
}

export const getOrderDetailPanelProps = (
  state: RootState,
): InjectedDetailProps => ({
  order: toOrderDetailPanelOrder(state.ordersSlice.selected),
  /**
   * The picked payment option's label, when the shopper's `PAYMENT_METHODS`
   * pick carried one. See `getOrderPaymentPickLabel` for the extraction and
   * `getOrderConfirmationPanelProps` for the longer rationale — this screen
   * is the one where the Redux fallback the cart uses cannot reach, since the
   * detail is routinely opened long after the pick's `checkoutSlice.available`
   * was populated. `undefined` leaves today's `paymentMethodLabels[method]`
   * lookup in charge, which is the "orders without a keyed pick show today's
   * label" acceptance criterion for COM-1259.
   */
  paymentMethodLabel: getOrderPaymentPickLabel(state.ordersSlice.selected),
  /**
   * The picked option's term, "14 días", for the tag beside the payment value
   * (COM-1710). Same source and rule as on `getOrderConfirmationPanelProps`:
   * the order's own pick, formatted by `formatTermDaysTag`. An order without
   * a keyed pick reads `undefined`, which draws no tag.
   */
  paymentMethodTag: formatTermDaysTag(
    getOrderPaymentPickAttributes(state.ordersSlice.selected)?.termDays,
    selectTermDaysTagTemplate(state),
  ),
  status: selectOrderDetailPanelStatus(state),
  /**
   * The tenant's zone, so an order's timestamp is not read in the viewer's.
   *
   * `configuration.i18n.timezone`, absent for most storefronts. `optional()`
   * because the field is nullable server-side *and* selected, so an authored-
   * but-empty value arrives as `null` rather than as a missing key — and the
   * panel's prop is `string | undefined`. Every other forwarded wire value in
   * these adapters goes through the same helper; this one is not an exception.
   *
   * No default: cinnamon does not know a tenant's zone, and the panel already
   * falls back to the runtime default, so an unset field behaves exactly as it
   * did before the prop existed.
   *
   * This does not reach the delivery date. That field names a calendar day and
   * the library renders it as stored, in UTC — passing a zone there would move
   * it to the previous day for any tenant west of UTC, which is COM-816
   * itself rather than its fix.
   */
  timeZone: optional(
    state.defaultSlice.sessionData?.configuration?.i18n?.timezone,
  ),
  /**
   * The supplying depot's name, or `null` for no row.
   *
   * The one injected prop that is **not** derived from the order in hand: it
   * comes from a `getStores` lookup that `useOrderDistributor` performs and
   * writes into the slice. See `selectOrderDistributor` above for the
   * code-match rule that makes reading it here safe, and for why every
   * unhappy path answers `null`.
   *
   * Injected rather than authorable for the same reason `order` is: no
   * `{{variable}}` can reach it, since the value is not on the order, not on
   * the config, and not in any state the DSL can address. The *label* beside
   * it stays in config, like every other label on this panel —
   * `{{texts.orders.detail.distributorLabel}}`, which now ships a default in
   * all seven languages.
   *
   * Outside the money gate on purpose. `maskPricesForDisplay` is an allowlist
   * of prop names over numbers and a depot name is neither, so it passes
   * through a hide-prices shopper's masking unchanged — which is correct: it
   * is not money and does not imply any.
   */
  distributor: selectOrderDistributor(state),
  /**
   * Slot text is a string, and `maskPricesForDisplay` is an allowlist of prop
   * names over numbers, so it cannot see a price inside `'Precio por kilo
   * $98.90'`. This is what the panel needs to suppress those; the numbers are
   * already covered by masking.
   */
  hidePrices: isHidePricesEnabled(state),
  declineReason: selectOrderDetailDeclineReason(state),
})

export default getOrderDetailPanelProps
